import { Component } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { IOption } from '@cmps/ng-select';
import { finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { SortDirection } from '../coaching-insight/oa/models/sorting.model';
import { CommonService } from '../common/common.service';
import { Util } from '../common/common.util';

import { TranslationKeyMap } from './reports.i18n';
import { UserReport } from './reports.model';
import { ReportsService } from './reports.service';
import { Sort } from './user-login.reports.model';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSortedData<T>(data: T[], sort: Sort) {
    if (!sort.active) {
        return data;
    }
    return data.sort((a, b) => {
        const fields = Object.getOwnPropertyNames(a);
        const selectedField = fields[fields.indexOf(sort.active)];
        if (typeof selectedField !== 'undefined') {
            return (
                (a[selectedField] < b[selectedField] ? -1 : 1) *
                (sort.direction === SortDirection.ASC ? 1 : -1)
            );
        }
        return 0;
    });
}

const dateRange = (startDateName: string, endDateName: string): ValidatorFn => {
    return (form: FormGroup): { [key: string]: boolean } | null => {
        const startDate = Util.dateToDate(form.controls[startDateName].value);
        const endDate = Util.dateToDate(form.controls[endDateName].value);
        if (endDate < startDate) {
            return { dateRange: true };
        }
        return null;
    };
};

@Component({
    selector: '[cmpsReportsUserLogin]',
    templateUrl: './user-login.reports.component.html',
    host: {
        class: 'cmps-reports-user-login',
    },
})
export class UserLoginReportsComponent {
    i18n = TranslationKeyMap;
    items: UserReport[];
    form: FormGroup;
    roles: IOption[];
    pendingData: boolean;
    pendingDetailData: boolean;
    data: { startTime: Date; endTime: Date; roles: string[] };
    sort: Sort;
    constructor(
        private readonly _reportService: ReportsService,
        private readonly _commonService: CommonService,
        private readonly _formBuilder: FormBuilder,
    ) {
        this.form = this._formBuilder.group(
            {
                startDate: [null, Validators.compose([Validators.required])],
                endDate: [null, Validators.compose([Validators.required])],
                roles: [null, Validators.compose([Validators.required])],
            },
            { validator: [dateRange('startDate', 'endDate')] },
        );
    }

    ngOnInit(): void {
        this.pendingData = true;
        this._commonService
            .listRoles()
            .pipe(
                take(1),
                map((roles) =>
                    roles.map((r) => ({ value: r.id, label: r.label })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((roles) => (this.roles = roles)),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onToggleDetail(item: UserReport) {
        if (item.expanded) {
            item.expanded = false;
            item.details = null;
            return;
        }
        this.items
            .filter((i) => i.expanded)
            .forEach((i) => {
                i.expanded = false;
                i.details = null;
            });
        item.expanded = true;
        this.pendingDetailData = true;
        this._reportService
            .retrieveUserInfoDetail(
                this.data.startTime,
                this.data.endTime,
                item.userId,
            )
            .pipe(
                take(1),
                // eslint-disable-next-line no-return-assign
                tap(
                    (details) =>
                        (item.details = getSortedData(details, {
                            active: 'loginDate',
                            direction: SortDirection.DESC,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingDetailData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGenerateClick() {
        if (this.form.invalid) {
            return;
        }
        this.data = {
            startTime: Util.dateToDate(this.form.get('startDate').value),
            endTime: Util.dateToDate(this.form.get('endDate').value),
            roles: this.form.get('roles').value as string[],
        };
        this.items = [];
        this.pendingData = true;
        this.sort = { active: 'userName', direction: SortDirection.ASC };
        this._reportService
            .retrieveUserInfo(
                this.data.startTime,
                this.data.endTime,
                this.data.roles.join(','),
            )
            .pipe(
                take(1),
                // eslint-disable-next-line no-return-assign
                tap((items) => (this.items = getSortedData(items, this.sort))),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getOpositeDirection(direction: SortDirection) {
        switch (direction) {
            case SortDirection.ASC:
                return SortDirection.DESC;
            case SortDirection.DESC:
                return SortDirection.ASC;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSort(active: string) {
        const direction =
            this.sort.active === active
                ? this.getOpositeDirection(this.sort.direction)
                : SortDirection.ASC;
        this.sort = { active, direction };
        this.items = getSortedData(this.items, this.sort);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const data = this.items.map((u) => {
            return {
                User: u.userName,
                Role: u.roleName,
                'Total Logins': u.loginAttemp,
            };
        });
        const workSheet: WorkSheet = utils.json_to_sheet(data);
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report User Login.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }
}
